import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ButtonControl, DialogControl, Loading } from "spark-core-dx/components";
import { useGenericManager } from "spark-core-dx/hooks";
import { Operations } from "../../../services/ItemServices";
import { getProduct, normalizeMarketplaceCatalog } from "../data/marketplaceCatalog";
import { invalidateMarketplaceCatalog } from "../marketplaceCheckout";
import "./marketplace.scss";

const PRODUCT_MARKETPLACE_CATALOG_ITEM = "ProductMarketplaceCatalog";
const RUNTIME_CAPACITY_OFFER_KIND = 4;

const getOperationItem = (response) => {
  const result = response?.first?.() ?? response?.[0] ?? response;
  return {
    result,
    item: result?.Items?.first?.() ?? result?.Items?.[0] ?? result?.Item ?? result?.item ?? result?.Value ?? null
  };
};

const getErrorMessage = (result, fallback) => {
  const messages = result?.Messages || result?.messages || [];
  const firstMessage = messages?.first?.() ?? messages?.[0] ?? result?.Message ?? result?.message;
  return firstMessage?.DisplayMessage || firstMessage?.displayMessage || firstMessage?.Message || firstMessage?.message || fallback;
};

const formatCurrency = (amount, currencyCode = "USD") => new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: currencyCode || "USD",
  maximumFractionDigits: Number(amount || 0) % 1 === 0 ? 0 : 2
}).format(Number(amount || 0));

const billingPeriodLabel = (value) => ({
  1: "Monthly",
  2: "Annual",
  3: "One time",
  4: "Manual"
}[Number(value || 0)] || "Not specified");

const priceLabel = (offer) => {
  if (offer?.reviewRequired) return "Review required";
  if (offer?.customPricing) return "Custom pricing";
  if (Number(offer?.baseAmount || 0) > 0) {
    return `${formatCurrency(offer.baseAmount, offer.currencyCode)} / ${billingPeriodLabel(offer.billingPeriod).toLowerCase()}`;
  }
  if (Number(offer?.unitRate || 0) > 0) {
    return `${formatCurrency(offer.unitRate, offer.currencyCode)} per ${offer.unitLabel || "unit"}`;
  }
  return offer?.priceSummary || "Pricing managed on offer";
};

const grantSummary = (offer, resource) => {
  const capacityGrant = (offer?.capacityGrants || []).find((grant) => grant.isActive !== false);
  if (capacityGrant) {
    if (capacityGrant.isUnlimited) return "Unlimited Processing Capacity";
    const maxConcurrentRuns = Math.max(1, Number(capacityGrant.maxConcurrentRuns || 1));
    return `${maxConcurrentRuns} active ${maxConcurrentRuns === 1 ? "run" : "runs"}`;
  }

  const resourceGrant = (offer?.resourceGrants || []).find((grant) =>
    !resource?.key
    || String(grant.productResourceKey || "").toLowerCase() === String(resource.key).toLowerCase()
    || Number(grant.grantRole || 0) === 1
  );
  if (resourceGrant) {
    const allowed = Math.max(1, Number(resourceGrant.allowedResourceConfigurationCount || 1));
    return `${allowed} Resource ${allowed === 1 ? "configuration" : "configurations"}`;
  }

  return Number(offer?.offerKind || 0) === 3
    ? "Unlimited eligible Resources"
    : "See Product Offer terms";
};

const buildSubmissionKey = (profileId, offerKey, resourceKey) => {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `${profileId || "profile"}:${offerKey}:${resourceKey || "capacity"}:${Date.now()}`;
};

const MarketplaceCheckout = ({
  panel = false,
  actionHost = null,
  offerKeyOverride = "",
  resourceKeyOverride = "",
  initialShowPaymentFollowUp = false,
  onExit
}) => {
  const catalogManager = useGenericManager(PRODUCT_MARKETPLACE_CATALOG_ITEM);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [actionError, setActionError] = useState("");
  const [showPaymentFollowUp, setShowPaymentFollowUp] = useState(initialShowPaymentFollowUp === true);
  const submissionKeyRef = useRef("");
  const offerKey = String(offerKeyOverride || "").trim();
  const resourceKey = String(resourceKeyOverride || "").trim();

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await catalogManager.RunOperation({
        operationName: Operations.ProductMarketplaceCatalogItem.LoadProductMarketplaceCatalog,
        item: { ItemName: PRODUCT_MARKETPLACE_CATALOG_ITEM },
        isItem: true,
        isWarning: true,
        supressAllErrors: true
      });
      const { result, item } = getOperationItem(response);
      if (!result?.Success || !item) {
        throw new Error(getErrorMessage(result, "The authoritative Marketplace offer could not be loaded."));
      }
      setCatalog(normalizeMarketplaceCatalog(item));
    } catch (error) {
      setLoadError(error?.message || "The authoritative Marketplace offer could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [catalogManager]);

  useEffect(() => {
    void loadCatalog();
  }, [loadCatalog]);

  const offer = useMemo(() => (catalog?.productOffers || []).find((candidate) =>
    String(candidate.key || "").toLowerCase() === String(offerKey).toLowerCase()
    || String(candidate.sku || "").toLowerCase() === String(offerKey).toLowerCase()
  ) || null, [catalog?.productOffers, offerKey]);
  const resource = useMemo(() => (catalog?.resources || []).find((candidate) =>
    String(candidate.key || "").toLowerCase() === String(resourceKey).toLowerCase()
  ) || null, [catalog?.resources, resourceKey]);
  const product = resource ? getProduct(resource.productKey, catalog?.products || []) : null;
  const purchaseName = resource?.name || product?.name || offer?.name || "";
  const purchaseOverview = product?.summary || resource?.summary || "";
  const purchaseIconPath = resource?.IconPath || product?.IconPath || "";
  const isRuntimeCapacity = Number(offer?.offerKind || 0) === RUNTIME_CAPACITY_OFFER_KIND;
  const actionText = catalog?.isSelfHostedResourceMode
    ? "Select"
    : "Checkout";

  const exitCheckout = () => {
    onExit?.();
  };

  const handleAction = async () => {
    if (!offer || busy) return;

    if (catalog?.isSelfHostedResourceMode) {
      setActionError("Self-Hosted Resource mode does not use payment checkout.");
      return;
    }

    setBusy(true);
    setActionError("");
    try {
      if (!submissionKeyRef.current) {
        submissionKeyRef.current = buildSubmissionKey("profile", offer.key, resource?.key || "");
      }
      const response = await catalogManager.RunOperation({
        operationName: Operations.ProductMarketplaceCatalogItem.SubmitMarketplacePaymentSupportRequest,
        item: {
          IsNew: true,
          EnvironmentKey: catalog.environmentKey || "",
          RequestedProductOfferKey: offer.key,
          RequestedProductResourceKey: resource?.key || "",
          SubmissionKey: submissionKeyRef.current
        },
        isItem: true,
        isWarning: true,
        supressAllErrors: true
      });

      const { result } = getOperationItem(response);
      if (!result?.Success) {
        throw new Error(getErrorMessage(result, "Marketplace checkout could not be completed."));
      }

      invalidateMarketplaceCatalog();
      setShowPaymentFollowUp(true);
    } catch (error) {
      setActionError(error?.message || "Marketplace checkout could not be completed.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <main className={`marketplace-checkout marketplace-checkout--state${panel ? " marketplace-checkout--panel" : ""}`}><Loading /></main>;
  }

  if (loadError || !offer || (resourceKey && !resource)) {
    return (
      <main className={`marketplace-checkout marketplace-checkout--state${panel ? " marketplace-checkout--panel" : ""}`}>
        <h1>Checkout unavailable</h1>
        <p>{loadError || (!offer ? "The selected Product Offer is not available." : "The selected Product Resource is not available.")}</p>
        <ButtonControl type="standard" onClick={exitCheckout}>Return</ButtonControl>
      </main>
    );
  }

  if (showPaymentFollowUp) {
    return (
      <main className={`marketplace-checkout${panel ? " marketplace-checkout--panel" : ""}`} aria-label="Marketplace payment next steps">
        <section className="marketplace-checkout__purchase-hero">
          <span className="marketplace-checkout__product-icon" aria-hidden="true">
            {purchaseIconPath ? <img src={purchaseIconPath} alt="" /> : null}
          </span>
          <div>
            <span className="marketplace-eyebrow">Next step: payment</span>
            <h2>Payment request received</h2>
            <p>
              Thank you for choosing Convoke. Your request for <strong>{purchaseName}</strong> has been received. Convoke Support will contact you to arrange payment and complete your purchase.
            </p>
          </div>
        </section>
        <section className="marketplace-checkout__product-overview">
          <span className="marketplace-eyebrow">Pending payment</span>
          <h3>Your payment request is pending</h3>
          <p>
            We&apos;ve sent your request to Convoke Support. A support team member will contact you to arrange payment. Access to this product will be available after payment is confirmed.
          </p>
          <div className="marketplace-checkout__selected-offer">
            <span>Selected offer</span>
            <strong>{offer.name}</strong>
          </div>
        </section>
        {panel
          ? (actionHost ? createPortal((
              <ButtonControl type="okay" onClick={exitCheckout}>Close</ButtonControl>
            ), actionHost) : null)
          : (
            <footer className="marketplace-checkout__actions">
              <ButtonControl type="okay" onClick={exitCheckout}>Close</ButtonControl>
            </footer>
          )}
      </main>
    );
  }

  return (
    <main className={`marketplace-checkout${panel ? " marketplace-checkout--panel" : ""}`} aria-label="Marketplace checkout">
      {!panel ? <header className="marketplace-checkout__header">
        <span className="marketplace-eyebrow">Marketplace Checkout</span>
        <h1>Review your purchase</h1>
        <p>Thank you for choosing Convoke. Review your product and purchase details before continuing.</p>
      </header> : (
        <section className="marketplace-checkout__purchase-hero">
          <span className="marketplace-checkout__product-icon" aria-hidden="true">
            {purchaseIconPath ? <img src={purchaseIconPath} alt="" /> : null}
          </span>
          <div>
            <span className="marketplace-eyebrow">Thank you for choosing Convoke</span>
            <h2>Review your purchase</h2>
            <p>
              Review what&apos;s included with <strong>{purchaseName}</strong> and confirm the purchase details below.
            </p>
            <div className="marketplace-checkout__selected-offer">
              <span>Selected offer</span>
              <strong>{offer.name}</strong>
            </div>
          </div>
        </section>
      )}
      {!catalog?.isSelfHostedResourceMode ? (
        <section className="marketplace-checkout__payment-notice" aria-labelledby="marketplace-checkout-payment-title">
          <span className="marketplace-eyebrow">How payment works</span>
          <h3 id="marketplace-checkout-payment-title">Checkout creates a payment request</h3>
          <p>
            <strong>No payment is collected now.</strong> Convoke Support will contact you to arrange payment. Access begins after payment is confirmed.
          </p>
        </section>
      ) : null}
      <section className="marketplace-checkout__product-overview">
        <span className="marketplace-eyebrow">Product overview</span>
        <h3>{purchaseName}</h3>
        {purchaseOverview ? <p>{purchaseOverview}</p> : null}
        <div className="marketplace-checkout__product-tags" aria-label="Purchase highlights">
          <span>{product?.name || "Marketplace Product"}</span>
          <span>{grantSummary(offer, resource)}</span>
        </div>
      </section>
      <section className="marketplace-checkout__summary">
        <header className="marketplace-checkout__summary-header">
          <div>
            <span className="marketplace-eyebrow">Purchase details</span>
            <h3>{offer.name}</h3>
          </div>
          <div className="marketplace-checkout__price">
            <span>Your price</span>
            <strong>{priceLabel(offer)}</strong>
            <small>{billingPeriodLabel(offer.billingPeriod)}</small>
          </div>
        </header>
        <dl>
          <div><dt>Product</dt><dd>{product?.name || (isRuntimeCapacity ? "Processing Capacity" : "Marketplace Product")}</dd></div>
          <div><dt>Selected resource</dt><dd>{resource?.name || (isRuntimeCapacity ? "Runtime processing capacity" : "Not specified")}</dd></div>
          <div><dt>Included grant or capacity</dt><dd>{grantSummary(offer, resource)}</dd></div>
          <div><dt>Quantity</dt><dd>1</dd></div>
          <div><dt>Billing</dt><dd>{billingPeriodLabel(offer.billingPeriod)}</dd></div>
          <div><dt>SKU</dt><dd>{offer.sku || offer.key}</dd></div>
          <div><dt>Environment</dt><dd>{catalog.environmentKey || "Current"}</dd></div>
        </dl>
      </section>
      {panel
        ? (actionHost ? createPortal((
            <>
              <ButtonControl type="cancel" onClick={exitCheckout} disabled={busy}>Cancel</ButtonControl>
              <ButtonControl type="okay" onClick={handleAction} disabled={busy} loading={busy}>{actionText}</ButtonControl>
            </>
          ), actionHost) : null)
        : (
          <footer className="marketplace-checkout__actions">
            <ButtonControl type="cancel" onClick={exitCheckout} disabled={busy}>Cancel</ButtonControl>
            <ButtonControl type="okay" onClick={handleAction} disabled={busy} loading={busy}>{actionText}</ButtonControl>
          </footer>
        )}
      <DialogControl
        controlOrigin="marketplace-checkout-error"
        title="Marketplace Checkout Error"
        openDialog={Boolean(actionError)}
        okText="Close"
        hideCancel
        onOk={() => setActionError("")}
        onCancel={() => setActionError("")}
        onX={() => setActionError("")}
      >
        <p>{actionError}</p>
      </DialogControl>
    </main>
  );
};

export default MarketplaceCheckout;
